import {
  useCallback,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
} from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Check,
  CheckSquare,
  ChevronLeft,
  Pencil,
  RotateCcw,
  Square,
  X,
} from 'lucide-react';
import { toast } from 'sonner';
import { useSearchParams } from 'react-router-dom';
import {
  ENRICHABLE_FIELDS,
  HISTORY_PAGE,
  MODULE_CATALOG,
  resolveFinalValue,
  type ProductSuggestionGroup,
  type SuggestionListItem,
  type ValidationCheck,
} from '@vce/shared';
import { PageHeader } from '@/app/layout';
import { useAuth } from '@/app/auth-context';
import { useTenantReady } from '@/app/tenant-context';
import { NoTenantState } from '@/components/no-tenant-state';
import { PaginationButtons } from '@/components/pagination-buttons';
import { ProductThumb } from '@/components/product-thumb';
import { ReviewHistoryPanel } from '@/components/review-history-panel';
import { CharacterCounter, DiffPanel, SpecSuggestionValueEditor } from '@/components/suggestion-diff';
import { displayValueForField } from '@/lib/spec-suggestion-display';
import {
  Badge,
  Button,
  Card,
  EmptyState,
  FIELD_LABEL,
  QueryState,
  Select,
  Spinner,
  Textarea,
} from '@/components/ui/primitives';
import { api, ApiError } from '@/lib/api';
import { useRunsModuleCta } from '@/lib/module-cta';
import { cn, formatNumber, formatRelative } from '@/lib/utils';

const PAGE_SIZE = 25;

type ReviewTab = 'pending' | 'blocked' | 'history';

const REVIEW_TABS: Array<{ id: ReviewTab; label: string }> = [
  { id: 'pending', label: 'Pendentes' },
  { id: 'blocked', label: 'Reprovadas' },
  { id: 'history', label: 'Histórico' },
];

function isReviewTab(value: string | null): value is ReviewTab {
  return value === 'pending' || value === 'blocked' || value === 'history';
}

function eligibleIds(suggestions: SuggestionListItem[]): string[] {
  return suggestions.filter((item) => item.validationReport.passed).map((item) => item.id);
}

export function ReviewPage() {
  const { tenantId, isResolving } = useTenantReady();
  const { can } = useAuth();
  const runsCta = useRunsModuleCta();
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const canReview = can('reviewer');

  const tabParam = searchParams.get('tab');
  const tab: ReviewTab = isReviewTab(tabParam) ? tabParam : 'pending';
  const queueStatus = tab === 'blocked' ? 'rejected_by_validator' : 'pending';
  const isQueueTab = tab === 'pending' || tab === 'blocked';

  const selectTab = useCallback(
    (next: ReviewTab) => {
      setSearchParams(next === 'pending' ? {} : { tab: next }, { replace: true });
      setPage(1);
      setSelectedProductId(null);
      setSelectedSuggestionId(null);
      setChecked(new Set());
    },
    [setSearchParams],
  );

  const [field, setField] = useState('');
  const [page, setPage] = useState(1);
  const [selectedProductId, setSelectedProductId] = useState<number | null>(null);
  const [selectedSuggestionId, setSelectedSuggestionId] = useState<string | null>(null);
  const [checked, setChecked] = useState<Set<string>>(new Set());

  const statsQuery = useQuery({
    queryKey: ['suggestions', tenantId, 'stats'],
    queryFn: () => api.suggestions.stats(tenantId as string),
    enabled: tenantId !== null,
  });

  const query = useQuery({
    queryKey: ['suggestions', tenantId, 'grouped', queueStatus, field, page],
    queryFn: () =>
      api.suggestions.listGrouped(tenantId as string, {
        status: queueStatus,
        field: field || undefined,
        page,
        pageSize: PAGE_SIZE,
      }),
    enabled: tenantId !== null && isQueueTab,
  });

  const groups = useMemo(() => query.data?.items ?? [], [query.data]);
  const pageEligibleIds = useMemo(
    () => groups.flatMap((group) => eligibleIds(group.suggestions)),
    [groups],
  );
  const allPageSelected =
    pageEligibleIds.length > 0 && pageEligibleIds.every((id) => checked.has(id));

  const preferredGroup =
    groups.find((group) => group.productId === selectedProductId) ?? null;
  const activeGroup = preferredGroup ?? groups[0] ?? null;
  const mobileDetailOpen = selectedProductId !== null && preferredGroup !== null;

  const preferredSuggestion =
    activeGroup?.suggestions.find((item) => item.id === selectedSuggestionId) ?? null;
  const activeSuggestion = preferredSuggestion ?? activeGroup?.suggestions[0] ?? null;

  function invalidate(): void {
    void queryClient.invalidateQueries({ queryKey: ['suggestions', tenantId] });
    void queryClient.invalidateQueries({ queryKey: ['health', tenantId] });
    void queryClient.invalidateQueries({ queryKey: ['runs', tenantId, 'write-preview'] });
    void queryClient.invalidateQueries({
      queryKey: ['runs', tenantId, 'generation-preview'],
    });
  }

  /** Próximo campo do mesmo produto; senão, primeiro campo do próximo produto. */
  function advance(current: SuggestionListItem): void {
    const groupIndex = groups.findIndex((group) => group.productId === current.productId);
    const group = groups[groupIndex];
    if (!group) {
      setSelectedSuggestionId(null);
      return;
    }

    const suggestionIndex = group.suggestions.findIndex((item) => item.id === current.id);
    const nextInGroup = group.suggestions[suggestionIndex + 1];
    if (nextInGroup) {
      setSelectedProductId(group.productId);
      setSelectedSuggestionId(nextInGroup.id);
      return;
    }

    const nextGroup = groups[groupIndex + 1] ?? groups[groupIndex - 1] ?? null;
    if (nextGroup) {
      setSelectedProductId(nextGroup.productId);
      setSelectedSuggestionId(nextGroup.suggestions[0]?.id ?? null);
      return;
    }

    setSelectedSuggestionId(null);
  }

  function toggleProduct(group: ProductSuggestionGroup): void {
    const ids = eligibleIds(group.suggestions);
    setChecked((previous) => {
      const next = new Set(previous);
      const allSelected = ids.length > 0 && ids.every((id) => next.has(id));
      for (const id of ids) {
        if (allSelected) next.delete(id);
        else next.add(id);
      }
      return next;
    });
  }

  function toggleSelectAll(): void {
    setChecked((previous) => {
      if (allPageSelected) {
        const next = new Set(previous);
        for (const id of pageEligibleIds) next.delete(id);
        return next;
      }
      const next = new Set(previous);
      for (const id of pageEligibleIds) next.add(id);
      return next;
    });
  }

  const review = useMutation({
    mutationFn: ({
      suggestion,
      decision,
      editedValue,
    }: {
      suggestion: SuggestionListItem;
      decision: 'approve' | 'reject';
      editedValue?: string | null;
    }) =>
      api.suggestions.review(tenantId as string, suggestion.id, {
        decision,
        editedValue: editedValue ?? null,
      }),
    onSuccess: (_data, variables) => {
      toast.success(variables.decision === 'approve' ? 'Sugestão aprovada' : 'Sugestão rejeitada');
      advance(variables.suggestion);
      invalidate();
    },
    onError: (error: unknown) => {
      toast.error(error instanceof ApiError ? error.message : 'Falha ao registrar a decisão');
    },
  });

  const bulkReview = useMutation({
    mutationFn: (decision: 'approve' | 'reject') =>
      api.suggestions.bulkReview(tenantId as string, {
        suggestionIds: [...checked],
        decision,
      }),
    onSuccess: (result) => {
      const skipped = result.skipped.length;
      toast.success(
        `${formatNumber(result.updated)} sugestões atualizadas` +
          (skipped > 0 ? ` · ${formatNumber(skipped)} ignoradas por reprovação no validador` : ''),
      );
      setChecked(new Set());
      invalidate();
    },
    onError: (error: unknown) => {
      toast.error(error instanceof ApiError ? error.message : 'Falha na revisão em lote');
    },
  });

  if (isResolving) {
    return <Spinner label="Carregando conta…" />;
  }

  if (!tenantId) {
    return <NoTenantState />;
  }

  const productTotal = query.data?.total ?? 0;
  const suggestionTotal = query.data?.suggestionTotal ?? 0;
  const pageCount = Math.max(1, Math.ceil(productTotal / PAGE_SIZE));
  const blockedCount = statsQuery.data?.byStatus?.rejected_by_validator ?? 0;

  const queueDescription =
    tab === 'blocked'
      ? productTotal === 0
        ? 'Nenhuma sugestão barrada pelos validadores.'
        : `${productTotal === 1 ? '1 produto' : `${formatNumber(productTotal)} produtos`} · ${suggestionTotal === 1 ? '1 sugestão' : `${formatNumber(suggestionTotal)} sugestões`} reprovadas automaticamente. Veja as checagens, edite e aprove ou rejeite.`
      : productTotal === 0
        ? 'Nenhum produto na fila.'
        : `${productTotal === 1 ? '1 produto' : `${formatNumber(productTotal)} produtos`} na fila · ${suggestionTotal === 1 ? '1 sugestão' : `${formatNumber(suggestionTotal)} sugestões`} para revisar. Nada vai para a VTEX sem aprovação.`;

  return (
    <>
      <PageHeader
        title={MODULE_CATALOG.review.label}
        description={tab === 'history' ? HISTORY_PAGE.description : queueDescription}
        actions={
          isQueueTab ? (
            <>
              <Select
                className="w-full sm:w-auto"
                value={field}
                onChange={(event) => {
                  setField(event.target.value);
                  setPage(1);
                  setSelectedProductId(null);
                  setSelectedSuggestionId(null);
                }}
              >
                <option value="">Todos os campos</option>
                {ENRICHABLE_FIELDS.map((item) => (
                  <option key={item} value={item}>
                    {FIELD_LABEL[item] ?? item}
                  </option>
                ))}
              </Select>
              {tab === 'pending' && checked.size > 0 && canReview ? (
                <>
                  <Button
                    variant="success"
                    size="sm"
                    disabled={bulkReview.isPending}
                    onClick={() => bulkReview.mutate('approve')}
                  >
                    <Check className="size-3.5" />
                    Aprovar {checked.size}
                  </Button>
                  <Button
                    variant="danger"
                    size="sm"
                    disabled={bulkReview.isPending}
                    onClick={() => bulkReview.mutate('reject')}
                  >
                    <X className="size-3.5" />
                    Rejeitar {checked.size}
                  </Button>
                </>
              ) : null}
            </>
          ) : undefined
        }
      />

      <ReviewTabs value={tab} onChange={selectTab} blockedCount={blockedCount} />

      {tab === 'history' ? (
        <div
          role="tabpanel"
          id="review-panel-history"
          aria-labelledby="review-tab-history"
        >
          <ReviewHistoryPanel embedded />
        </div>
      ) : (
      <div
        role="tabpanel"
        id={`review-panel-${tab}`}
        aria-labelledby={`review-tab-${tab}`}
      >
      <QueryState
        isLoading={query.isLoading}
        isError={query.isError}
        loadingLabel="Carregando sugestões…"
        errorTitle="Não foi possível carregar a fila"
        onRetry={() => void query.refetch()}
      >
        {groups.length === 0 ? (
          <EmptyState
            title={tab === 'blocked' ? 'Nenhuma reprovada' : 'Fila vazia'}
            description={
              tab === 'blocked'
                ? 'Quando a geração passar pelos validadores com falha bloqueante, a sugestão aparece aqui com o relatório de erros — não some do sistema.'
                : runsCta.description(
                    `Gere sugestões em ${MODULE_CATALOG.runs.label} para popular a fila de revisão. Se a geração marcar Ignorados, veja a aba Reprovadas.`,
                  )
            }
            action={tab === 'pending' ? runsCta.action() : undefined}
          />
        ) : (
          <div className="flex h-[calc(100dvh-10.5rem)] flex-col overflow-hidden md:grid md:h-[calc(100dvh-7.5rem)] md:grid-cols-[min(340px,38vw)_1fr]">
            <aside
              className={cn(
                'flex min-h-0 flex-col overflow-hidden border-b border-[var(--color-hairline)] bg-[var(--color-surface-soft)] md:border-b-0 md:border-r',
                mobileDetailOpen ? 'hidden md:flex' : 'flex',
              )}
            >
              {tab === 'pending' ? (
                <div className="flex items-center justify-between gap-2 border-b border-[var(--color-hairline)] px-3 py-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    disabled={pageEligibleIds.length === 0}
                    onClick={toggleSelectAll}
                    aria-pressed={allPageSelected}
                  >
                    {allPageSelected ? (
                      <CheckSquare className="size-3.5" />
                    ) : (
                      <Square className="size-3.5" />
                    )}
                    {allPageSelected ? 'Desselecionar' : 'Selecionar todos'}
                  </Button>
                  {checked.size > 0 ? (
                    <span className="text-[11px] text-[var(--color-muted)]">
                      {formatNumber(checked.size)} selecionadas
                    </span>
                  ) : null}
                </div>
              ) : (
                <div className="border-b border-[var(--color-hairline)] px-3 py-2">
                  <p className="text-[11px] leading-snug text-[var(--color-muted)]">
                    Barradas pelos validadores. Edite o texto e aprove, ou rejeite.
                  </p>
                </div>
              )}

              <div className="min-h-0 flex-1 divide-y divide-[var(--color-hairline)] overflow-y-auto">
                {groups.map((group) => {
                  const ids = eligibleIds(group.suggestions);
                  const selectedCount = ids.filter((id) => checked.has(id)).length;
                  const isChecked =
                    ids.length > 0 && selectedCount === ids.length
                      ? true
                      : selectedCount > 0
                        ? 'indeterminate'
                        : false;

                  return (
                    <ProductQueueRow
                      key={group.productId}
                      group={group}
                      isActive={group.productId === activeGroup?.productId}
                      checked={isChecked}
                      showCheckbox={tab === 'pending'}
                      onSelect={() => {
                        setSelectedProductId(group.productId);
                        setSelectedSuggestionId(group.suggestions[0]?.id ?? null);
                      }}
                      onToggle={() => toggleProduct(group)}
                    />
                  );
                })}
              </div>

              <PaginationButtons
                className="justify-between border-t border-[var(--color-hairline)] px-3 py-2"
                page={page}
                pageCount={pageCount}
                onPrev={() => setPage((current) => current - 1)}
                onNext={() => setPage((current) => current + 1)}
              />
            </aside>

            <section
              className={cn(
                'min-w-0 flex-1 overflow-y-auto bg-[var(--color-canvas)] p-4 sm:p-6',
                mobileDetailOpen ? 'block' : 'hidden md:block',
              )}
            >
              {activeGroup && activeSuggestion ? (
                <div className="w-full">
                  <button
                    type="button"
                    className="mb-3 inline-flex cursor-pointer items-center gap-1 text-xs font-medium text-[var(--color-primary)] md:hidden"
                    onClick={() => setSelectedProductId(null)}
                  >
                    <ChevronLeft className="size-3.5" />
                    Voltar à lista
                  </button>
                  <ProductReviewDetail
                    group={activeGroup}
                    activeSuggestion={activeSuggestion}
                    canReview={canReview}
                    isPending={review.isPending}
                    machineBlocked={tab === 'blocked'}
                    onSelectSuggestion={(id) => {
                      setSelectedProductId(activeGroup.productId);
                      setSelectedSuggestionId(id);
                    }}
                    onApprove={(editedValue) =>
                      review.mutate({
                        suggestion: activeSuggestion,
                        decision: 'approve',
                        editedValue,
                      })
                    }
                    onReject={() =>
                      review.mutate({ suggestion: activeSuggestion, decision: 'reject' })
                    }
                  />
                </div>
              ) : (
                <EmptyState title="Selecione um produto" />
              )}
            </section>
          </div>
        )}
      </QueryState>
      </div>
      )}
    </>
  );
}

function ReviewTabs({
  value,
  onChange,
  blockedCount,
}: {
  value: ReviewTab;
  onChange(tab: ReviewTab): void;
  blockedCount: number;
}) {
  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
    const index = REVIEW_TABS.findIndex((item) => item.id === value);
    if (index < 0) return;
    if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
      event.preventDefault();
      const delta = event.key === 'ArrowRight' ? 1 : -1;
      const next = REVIEW_TABS[(index + delta + REVIEW_TABS.length) % REVIEW_TABS.length]!;
      onChange(next.id);
      document.getElementById(`review-tab-${next.id}`)?.focus();
    }
    if (event.key === 'Home') {
      event.preventDefault();
      onChange(REVIEW_TABS[0]!.id);
      document.getElementById(`review-tab-${REVIEW_TABS[0]!.id}`)?.focus();
    }
    if (event.key === 'End') {
      event.preventDefault();
      const last = REVIEW_TABS[REVIEW_TABS.length - 1]!;
      onChange(last.id);
      document.getElementById(`review-tab-${last.id}`)?.focus();
    }
  };

  return (
    <div
      role="tablist"
      aria-label="Fila e histórico de revisão"
      onKeyDown={onKeyDown}
      className="flex gap-1 overflow-x-auto border-b border-[var(--color-hairline)] px-4 sm:px-6"
    >
      {REVIEW_TABS.map((item) => {
        const selected = item.id === value;
        return (
          <button
            key={item.id}
            type="button"
            role="tab"
            id={`review-tab-${item.id}`}
            aria-selected={selected}
            aria-controls={`review-panel-${item.id}`}
            tabIndex={selected ? 0 : -1}
            onClick={() => onChange(item.id)}
            className={cn(
              'inline-flex shrink-0 items-center gap-1.5 border-b-2 px-3 py-2.5 text-sm transition',
              'focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-[var(--color-primary)]',
              selected
                ? 'border-[var(--color-primary)] font-semibold text-[var(--color-ink)]'
                : 'border-transparent font-medium text-[var(--color-muted)] hover:text-[var(--color-ink)]',
            )}
          >
            {item.label}
            {item.id === 'blocked' && blockedCount > 0 ? (
              <Badge tone="critical">{formatNumber(blockedCount)}</Badge>
            ) : null}
          </button>
        );
      })}
    </div>
  );
}

/** Two rows of 11px badges (`py-0.5` + ~16px type) plus one `gap-1`. */
const QUEUE_TAGS_COLLAPSED_CLASS = 'max-h-[3rem] overflow-hidden';

function ProductQueueRow({
  group,
  isActive,
  checked,
  showCheckbox = true,
  onSelect,
  onToggle,
}: {
  group: ProductSuggestionGroup;
  isActive: boolean;
  checked: boolean | 'indeterminate';
  showCheckbox?: boolean;
  onSelect(): void;
  onToggle(): void;
}) {
  const hasEligible = eligibleIds(group.suggestions).length > 0;
  const warningCount = group.suggestions.reduce(
    (sum, item) => sum + item.validationReport.warnings.length,
    0,
  );
  const suggestionKey = group.suggestions.map((item) => item.id).join(',');
  const tagsRef = useRef<HTMLDivElement>(null);
  const [expanded, setExpanded] = useState(false);
  const [canExpand, setCanExpand] = useState(false);

  useLayoutEffect(() => {
    setExpanded(false);
    setCanExpand(false);
  }, [suggestionKey]);

  useLayoutEffect(() => {
    if (expanded) return;
    const el = tagsRef.current;
    if (!el) return;

    const measure = (): void => {
      setCanExpand(el.scrollHeight > el.clientHeight + 1);
    };

    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [expanded, suggestionKey, group.blockedCount, warningCount]);

  return (
    <div
      className={cn(
        'flex cursor-pointer items-start gap-2 px-3 py-2.5 transition',
        isActive
          ? 'bg-[var(--color-surface-card)]'
          : 'hover:bg-[var(--color-surface-elevated)]',
      )}
      onClick={onSelect}
    >
      {showCheckbox ? (
        <input
          type="checkbox"
          className="mt-1 size-3.5 shrink-0 cursor-pointer accent-[var(--color-primary)] disabled:cursor-not-allowed"
          checked={checked === true}
          ref={(element) => {
            if (element) element.indeterminate = checked === 'indeterminate';
          }}
          disabled={!hasEligible}
          onClick={(event) => event.stopPropagation()}
          onChange={onToggle}
          aria-label={`Selecionar sugestões de ${group.productName}`}
        />
      ) : null}
      <ProductThumb src={group.imageUrl} alt={group.productName} />
      <div className="min-w-0 flex-1">
        <p className="truncate text-xs font-medium text-[var(--color-ink)]">{group.productName}</p>
        <p className="truncate text-[11px] text-[var(--color-muted)]">
          {group.categoryName ?? 'sem categoria'} · #{group.productId} ·{' '}
          {formatNumber(group.suggestions.length)}{' '}
          {group.suggestions.length === 1 ? 'sugestão' : 'sugestões'}
        </p>
        <div
          ref={tagsRef}
          className={cn(
            'mt-1 flex flex-wrap items-center gap-1',
            expanded ? null : QUEUE_TAGS_COLLAPSED_CLASS,
          )}
        >
          {group.suggestions.map((item) => (
            <Badge key={item.id} tone="brand">
              {FIELD_LABEL[item.field] ?? item.field}
            </Badge>
          ))}
          {group.blockedCount > 0 ? (
            <Badge tone="critical">
              {formatNumber(group.blockedCount)} reprovada
              {group.blockedCount === 1 ? '' : 's'}
            </Badge>
          ) : null}
          {warningCount > 0 ? (
            <Badge tone="medium">
              {formatNumber(warningCount)} aviso{warningCount === 1 ? '' : 's'}
            </Badge>
          ) : null}
        </div>
        {canExpand ? (
          <button
            type="button"
            aria-expanded={expanded}
            className="mt-1 cursor-pointer text-[11px] font-medium text-[var(--color-primary)] hover:underline"
            onClick={(event) => {
              event.stopPropagation();
              setExpanded((current) => !current);
            }}
          >
            {expanded ? 'Ver menos' : 'Ver mais'}
          </button>
        ) : null}
      </div>
    </div>
  );
}

function ProductReviewDetail({
  group,
  activeSuggestion,
  canReview,
  isPending,
  machineBlocked = false,
  onSelectSuggestion,
  onApprove,
  onReject,
}: {
  group: ProductSuggestionGroup;
  activeSuggestion: SuggestionListItem;
  canReview: boolean;
  isPending: boolean;
  machineBlocked?: boolean;
  onSelectSuggestion(id: string): void;
  onApprove(editedValue: string | null): void;
  onReject(): void;
}) {
  return (
    <div className="mx-auto max-w-5xl space-y-4">
      <div className="flex items-start gap-3">
        <ProductThumb src={group.imageUrl} alt={group.productName} />
        <div className="min-w-0">
          <h2 className="text-base font-semibold text-[var(--color-ink)]">{group.productName}</h2>
          <p className="mt-1 text-xs text-[var(--color-muted)]">
            Produto #{group.productId} · {group.categoryName ?? 'sem categoria'} ·{' '}
            {group.brandName ?? 'sem marca'} · {formatNumber(group.suggestions.length)} sugestões
          </p>
        </div>
      </div>

      <div className="flex flex-wrap gap-1.5">
        {group.suggestions.map((item) => {
          const blocked = !item.validationReport.passed || machineBlocked;
          const isActive = item.id === activeSuggestion.id;
          return (
            <button
              key={item.id}
              type="button"
              onClick={() => onSelectSuggestion(item.id)}
              className={cn(
                'inline-flex cursor-pointer items-center gap-1.5 rounded-[10px] border px-2.5 py-1.5 text-[11px] font-medium transition',
                isActive
                  ? 'border-[var(--color-primary)] bg-[color-mix(in_srgb,var(--color-primary)_12%,transparent)] text-[var(--color-ink)]'
                  : 'border-[var(--color-hairline)] bg-[var(--color-surface-card)] text-[var(--color-muted)] hover:bg-[var(--color-surface-elevated)] hover:text-[var(--color-ink)]',
              )}
            >
              {FIELD_LABEL[item.field] ?? item.field}
              {blocked ? <span className="text-[var(--color-error)]">·</span> : null}
            </button>
          );
        })}
      </div>

      <SuggestionDetail
        key={activeSuggestion.id}
        suggestion={activeSuggestion}
        canReview={canReview}
        isPending={isPending}
        machineBlocked={machineBlocked}
        onApprove={onApprove}
        onReject={onReject}
      />
    </div>
  );
}

function SuggestionDetail({
  suggestion,
  canReview,
  isPending,
  machineBlocked = false,
  onApprove,
  onReject,
}: {
  suggestion: SuggestionListItem;
  canReview: boolean;
  isPending: boolean;
  machineBlocked?: boolean;
  /** `null` significa aprovar o texto original da IA, sem edição. */
  onApprove(editedValue: string | null): void;
  onReject(): void;
}) {
  // Reprovadas pela máquina: abre já em edição para forçar revisão do texto.
  const [draft, setDraft] = useState<string | null>(() =>
    machineBlocked ? resolveFinalValue(suggestion) : null,
  );
  const rawProposed = draft ?? resolveFinalValue(suggestion);
  const proposed = displayValueForField(suggestion.field, rawProposed);
  const current = displayValueForField(suggestion.field, suggestion.currentValue ?? '');
  const isEditing = draft !== null;
  const blocked = !suggestion.validationReport.passed || machineBlocked;
  const blockingFailures = suggestion.validationReport.checks.filter(
    (check) => !check.passed && check.level === 'blocking',
  );
  const warningFailures = suggestion.validationReport.checks.filter(
    (check) => !check.passed && check.level === 'warning',
  );
  const showWarningBanner = !blocked && warningFailures.length > 0;

  return (
    <div className="space-y-4">
      <div>
        <div className="flex flex-wrap items-center gap-2">
          <Badge tone="brand">{FIELD_LABEL[suggestion.field] ?? suggestion.field}</Badge>
          {machineBlocked ? <Badge tone="critical">reprovada pela máquina</Badge> : null}
          {suggestion.skuId !== null ? <Badge tone="neutral">SKU {suggestion.skuId}</Badge> : null}
          {suggestion.fileId != null ? (
            <Badge tone="neutral">arquivo {suggestion.fileId}</Badge>
          ) : null}
        </div>
        {suggestion.imageUrl ? (
          <div className="mt-3 overflow-hidden rounded-2xl border border-[var(--color-hairline)] bg-[var(--color-surface-elevated)]">
            <img
              src={suggestion.imageUrl}
              alt={suggestion.currentValue ?? 'Imagem do SKU'}
              className="mx-auto max-h-48 object-contain"
            />
          </div>
        ) : null}
        <p className="mt-1 text-xs text-[var(--color-muted)]">
          Gerada {formatRelative(suggestion.createdAt)}
          {suggestion.usage
            ? ` · ${suggestion.usage.model} (prompt ${suggestion.usage.promptVersion})`
            : ''}
        </p>
      </div>

      {blocked ? (
        <div className="space-y-2 rounded-[10px] border border-[color-mix(in_srgb,var(--color-error)_40%,transparent)] bg-[color-mix(in_srgb,var(--color-error)_12%,transparent)] px-4 py-3">
          <p className="text-xs font-medium text-[var(--color-error)]">
            {machineBlocked
              ? 'Barrada pelos validadores na geração. Edite o texto e aprove, ou rejeite.'
              : 'Reprovada pelos validadores determinísticos. Aprovar exige editar o texto.'}
          </p>
          {blockingFailures.length > 0 ? (
            <ul className="list-disc space-y-1 pl-4 text-[11px] text-[var(--color-error)]">
              {blockingFailures.map((check) => (
                <li key={check.id}>
                  <span className="font-medium">{check.label}</span>
                  {check.message ? ` — ${check.message}` : ''}
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      ) : null}

      {showWarningBanner ? (
        <div className="space-y-2 rounded-[10px] border border-[color-mix(in_srgb,var(--color-warning,_#d97706)_40%,transparent)] bg-[color-mix(in_srgb,var(--color-warning,_#d97706)_12%,transparent)] px-4 py-3">
          <p className="text-xs font-medium text-[var(--color-warning,_#b45309)]">
            Atenção (não impede aprovação) — boas práticas SEO ou estilo.
          </p>
          <ul className="list-disc space-y-1 pl-4 text-[11px] text-[var(--color-warning,_#b45309)]">
            {warningFailures.map((check) => (
              <li key={check.id}>
                <span className="font-medium">{check.label}</span>
                {check.message ? ` — ${check.message}` : ''}
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      <div className="grid gap-3 lg:grid-cols-2">
        <Card className="flex flex-col">
          <div className="flex items-center justify-between border-b border-[var(--color-hairline)] px-4 py-2">
            <span
              className="text-xs font-medium text-[var(--color-muted)]"
              title="Baseline do snapshot na geração — não é leitura ao vivo da VTEX"
            >
              Valor na geração
            </span>
            <CharacterCounter field={suggestion.field} value={current} />
          </div>
          <div className="flex-1 px-4 py-3">
            <DiffPanel
              before={suggestion.currentValue ?? ''}
              after={rawProposed}
              side="before"
              field={suggestion.field}
            />
          </div>
        </Card>

        <Card className="flex flex-col">
          <div className="flex items-center justify-between border-b border-[var(--color-hairline)] px-4 py-2">
            <span className="text-xs font-medium text-[var(--color-muted)]">
              {isEditing ? 'Sua edição' : 'Sugerido'}
            </span>
            <div className="flex items-center gap-3">
              <CharacterCounter field={suggestion.field} value={proposed} />
              {canReview ? (
                <button
                  type="button"
                  className="inline-flex cursor-pointer items-center gap-1 text-[11px] text-[var(--color-primary)] hover:underline"
                  onClick={() => setDraft(isEditing ? null : resolveFinalValue(suggestion))}
                >
                  {isEditing ? <RotateCcw className="size-3" /> : <Pencil className="size-3" />}
                  {isEditing ? 'Descartar edição' : 'Editar'}
                </button>
              ) : null}
            </div>
          </div>
          <div className="flex-1 px-4 py-3">
            {isEditing ? (
              suggestion.field === 'specifications' ? (
                <SpecSuggestionValueEditor
                  draft={draft ?? ''}
                  onDraftChange={(next) => setDraft(next)}
                />
              ) : (
                <Textarea
                  rows={12}
                  value={draft ?? ''}
                  autoFocus
                  onChange={(event) => setDraft(event.target.value)}
                />
              )
            ) : (
              <DiffPanel
                before={suggestion.currentValue ?? ''}
                after={rawProposed}
                side="after"
                field={suggestion.field}
              />
            )}
          </div>
        </Card>
      </div>

      {suggestion.rationale ? (
        <Card>
          <h3 className="border-b border-[var(--color-hairline)] px-4 py-2 text-xs font-medium text-[var(--color-muted)]">
            Justificativa do modelo
          </h3>
          <p className="px-4 py-3 text-sm leading-relaxed text-[var(--color-ink)]">
            {suggestion.rationale}
          </p>
        </Card>
      ) : null}

      <Card>
        <div className="border-b border-[var(--color-hairline)] px-4 py-2 text-xs font-medium text-[var(--color-muted)]">
          Checagens determinísticas
        </div>
        <div className="divide-y divide-[var(--color-hairline)]">
          {suggestion.validationReport.checks.map((check) => (
            <CheckRow key={check.id} check={check} />
          ))}
        </div>
      </Card>

      {canReview ? (
        <div className="sticky bottom-0 flex flex-col gap-3 border-t border-[var(--color-hairline)] bg-[var(--color-canvas)] px-1 py-3 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-[11px] text-[var(--color-muted)]">
            {isEditing
              ? 'Aprovar registra o campo como conteúdo editado por humano sobre base de IA.'
              : 'Aprovar registra o campo como gerado por IA no feed do Merchant Center.'}
          </p>
          <div className="flex w-full gap-2 sm:w-auto">
            <Button
              variant="danger"
              className="flex-1 sm:flex-none"
              disabled={isPending}
              onClick={onReject}
            >
              <X className="size-4" />
              Rejeitar
            </Button>
            <Button
              variant="success"
              className="flex-1 sm:flex-none"
              disabled={isPending || (blocked && !isEditing) || rawProposed.trim().length === 0}
              onClick={() =>
                onApprove(
                  draft !== null && draft !== resolveFinalValue(suggestion) ? draft : null,
                )
              }
            >
              <Check className="size-4" />
              Aprovar
            </Button>
          </div>
        </div>
      ) : (
        <p className="text-xs text-[var(--color-muted)]">
          Seu perfil é somente leitura. Peça a um revisor para decidir.
        </p>
      )}
    </div>
  );
}

function CheckRow({ check }: { check: ValidationCheck }) {
  const tone = check.passed ? 'success' : check.level === 'blocking' ? 'critical' : 'medium';
  const label = check.passed ? 'ok' : check.level === 'blocking' ? 'bloqueia' : 'aviso';
  return (
    <div className="flex items-start gap-3 px-4 py-2">
      <Badge tone={tone} className="mt-0.5 shrink-0">
        {label}
      </Badge>
      <div className="min-w-0">
        <p className="text-xs font-medium text-[var(--color-ink)]">{check.label}</p>
        {check.message ? (
          <p className="text-[11px] text-[var(--color-muted)]">{check.message}</p>
        ) : null}
      </div>
    </div>
  );
}
